import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isNil } from 'lodash'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'

import { applyMigration } from '@/reducers/costimizer/costimizer.actions'
import { selectRun } from '@/reducers/costimizer/costimizer.redux'

import { RECOMMENDED_TOLERANCE } from '../Costimizer.helpers'

/**
 * Put ONE prompt live, whatever the threshold says.
 *
 * ⭐ A threshold is all-or-nothing, and a reader's judgement is not: two prompts can measure the same 10
 * points worse while only one of them is acceptable, once somebody has read the calls. Without this the only
 * way to take that one is to loosen the threshold for every prompt beside it.
 *
 * The threshold still decides the batch below the table. This is the escape hatch from it, so the
 * confirmation says what the prompt measured rather than assuming the reader remembers.
 *
 * @param {object} props.criterion the result to publish, as `toCriterionResult` shapes it
 */
export const ApplyPromptButton = ({ scorecardConfigId, criterion }) => {
  const dispatch = useDispatch()
  const run = useSelector(selectRun(scorecardConfigId))
  const [confirming, setConfirming] = useState(false)

  const worse = !isNil(criterion.marginPct) && criterion.marginPct < 0
  const outsideRecommended = criterion.marginPct < -RECOMMENDED_TOLERANCE

  const handleApply = () => {
    dispatch(applyMigration(scorecardConfigId, { criteria: [criterion] }))
    setConfirming(false)
  }

  return (
    <>
      <Button
        small
        loading={run.applying}
        onClick={() => setConfirming(true)}
        data-testid={`costimizer-apply-subject-${criterion.key}`}
      >
        Migrate
      </Button>

      <BasicModal
        title="Migrate this prompt?"
        show={confirming}
        onClose={() => setConfirming(false)}
        size="tiny"
        data-testid="costimizer-apply-subject-confirm"
      >
        <p>
          {`This puts ${criterion.name ?? 'this prompt'} live on ${criterion.newModel ?? 'the cheaper model'}. Calls scored from now on will use it.`}
        </p>
        {worse && (
          <p>
            {`It measured ${Math.abs(criterion.marginPct).toFixed(1)} points worse than the prompt running now`}
            {outsideRecommended
              ? ` — further off than a re-run of the current prompt explains.`
              : `, which is inside the ${RECOMMENDED_TOLERANCE}-point allowance.`}
          </p>
        )}
        <div className="form-actions is-modal">
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button brand onClick={handleApply} data-testid="costimizer-apply-subject-confirm-button">
            Migrate
          </Button>
        </div>
      </BasicModal>
    </>
  )
}
